import React from 'react';
import { fromLonLat } from 'ol/proj';
import LineString from 'ol/geom/LineString';
import GeometryLayout from 'ol/geom/GeometryLayout';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import 'ol/ol.css';
import Feature from 'ol/Feature';
import { scaleLane, generateBezierCurve } from "Common/Map/Utilities";
import { GetLaneColor } from 'Common/Map/Utilities/Tss/LaneUtilities';
import { Id, Lane, OpStatus, LanePollData, Midpoint, LinkGeometry, Node } from "NSwag/ServerClient/Client.nswag";

export interface IProps {
    lane: Lane,
    index: number,
    opStatus: OpStatus,
    pollData: LanePollData,
    speedLimit: number,
    previousMidPoint: Midpoint | null,
    nextMidPoint: Midpoint | null,
    linkGeometry: LinkGeometry | null,
    startNode: Node | null,
    endNode: Node | null,
    zoomLevel: number,
    onLineStringAdded?: (lineString: LineString) => void;
}

interface IState {
    feature: Feature | null,
    stroke: Stroke | null,
    smoothedGeometry: number[][] | null,
    laneColor: string | null
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class TssLane extends React.Component<IProps, IState> {

    id!: Id;

    props!: IProps;

    state!: IState;

    /**
    * Set the initial state
    * @return { void }
    **/
    constructor(props: IProps) {
        super(props);
        if (props.lane != null) {
            this.id = props.lane.id!;
        }

        this.state = {
            feature: null,
            stroke: null,
            smoothedGeometry: null,
            laneColor: null
        }
    }

    /**
     * React Lifecycle event
     */
    componentDidMount = () => {

        const { startNode, endNode, linkGeometry } = this.props;

        if (startNode && endNode && linkGeometry) {
            this.addTssLaneToVectorLayer();
        }

    }

    componentDidUpdate = (prevProps: IProps) => {

        const { startNode, endNode, linkGeometry } = this.props;

        if (!this.state.feature && startNode && endNode && linkGeometry) {

            this.addTssLaneToVectorLayer();

        }

        if (this.state.feature
            && (
                (prevProps.pollData == null && this.props.pollData != null)
                || (prevProps.pollData != null && prevProps.pollData !== this.props.pollData)
                || prevProps.opStatus !== this.props.opStatus
            )
        ) {
            this.updateLaneColor();
        }

    }

    componentWillUnmount = () => {

        const { feature} = this.state;

        if (feature) {
            (this.props as any).vectorLayerSource.removeFeature(feature);
        }

    }

    /**
     * Method fired before component re-renders to determine if it should re-render or not
     * @returns {boolean} - true if component needs to be re-rendered; false if not.
     */
    shouldComponentUpdate = (nextProps: IProps, nextState: IState) => {
        let retVal = false;
        if (this.state.laneColor !== this.getLaneColor(nextProps)) {
            retVal = true;
        }

        if (this.props.zoomLevel !== nextProps.zoomLevel) {
            retVal = true;
        }
        return retVal;

    }

    /**
     * Gets lane color given props
     */
    getLaneColor = (props: IProps) => {
        return GetLaneColor(props.opStatus, props.speedLimit, props.pollData);
    }

    /**
    * Adds the current lane to the vectorLayer
    * @return { void }
    */
    addTssLaneToVectorLayer = () => {

        // change the projection of the coordinates to work with our map
        const { startNode, endNode, linkGeometry, index, zoomLevel } = this.props;

        let formattedMidPoints = linkGeometry!.midpoints!.map(midPoint => [midPoint.latitude, midPoint.longitude]);

        // Formats start node, midpoints and end node lat longs into [ [lat, long], ... ]
        let laneGeometry = [
            [startNode!.latitude, startNode!.longitude],
            ...formattedMidPoints,
            [endNode!.latitude, endNode!.longitude]
        ];

        let formattedGeometry = laneGeometry.map(coord => [coord[0] / 1000000.0, coord[1] / 1000000.0]);
        let smoothedGeometry = generateBezierCurve(formattedGeometry, index).map(coord => fromLonLat([coord[1], coord[0]]));
        let line = new LineString(smoothedGeometry);

        let laneColor = this.getLaneColor(this.props);

        let stroke = new Stroke({
            color: laneColor,
            lineCap: "butt",
            lineJoin: "bevel",
            width: scaleLane(zoomLevel)
        });

        let style = new Style({
            stroke
        });

        let feature = new Feature({
            geometry: line,
            name: 'Line'
        });

        feature.setStyle(style);
        (this.props as any).vectorLayerSource.addFeature(feature);

        this.setState({ feature, stroke, laneColor }, () => this.handleLineSet(line));
    }

    handleLineSet = (line: LineString) => {
        const { onLineStringAdded } = this.props;
        if (onLineStringAdded) {
            onLineStringAdded(line);
        }
    }

    updateLaneColor = () => {

        const { opStatus, speedLimit, pollData, zoomLevel } = this.props;
        const { laneColor } = this.state;

        if (opStatus) {
            let newLaneColor = GetLaneColor(opStatus, speedLimit, pollData);
            if (newLaneColor !== laneColor) {

                console.log(newLaneColor);

                let stroke = new Stroke({
                    color: newLaneColor,
                    lineCap: "butt",
                    lineJoin: "bevel",
                    width: scaleLane(zoomLevel)
                });

                let style = new Style({
                    stroke
                });

                this.state.feature!.setStyle(style);

                this.setState({ laneColor: newLaneColor });
            }
        }
    }

    /**
    * Renders the polyline for a lane
    * @returns {Polyline} - The rendered polyline for a lane
    */
    render() {
        const { zoomLevel } = this.props;
        if (this.state.stroke != null) {
            this.state.stroke.setWidth(scaleLane(zoomLevel));
        }
        return null;
    }

}

/*
* Make our component the default export object and attach the styles defined
*/
export default TssLane